import React, { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useParams, useLocation, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import styled from 'styled-components';
import { LayoutGrid, Star, ArrowRight } from 'lucide-react';
import { Container } from '../../components/styled/Container';
import { PageLayout } from '../../components/styled/PageLayout';
import { regionFromPathname } from '../../utils/regionFromPath';
import { serverLanguageFromRegion } from '../../utils/serverLanguage';
import { CATEGORIES_TYPES } from '../../utils/categoriesPaths';
import { fetchCatalog } from '../../store/actions/catalogActions';
import { similarPath } from '../../utils/paths';
import Pagination, { PAGE_SIZE } from '../../components/categories/Pagination';
import SortBar, { sortItems, SORT_OPTIONS } from '../../components/categories/SortBar';
import NotFound from '../NotFound/NotFound';
import SEO from '../../components/seo/SEO';
import { useI18n } from '../../hooks/useI18n';
import StaticAdBanner from '../../components/ads/StaticAdBanner';
import AdCarousel from '../../components/ads/AdCarousel';
import AdInline from '../../components/ads/AdInline';
import { bannerAds, carouselAds, cardAds } from '../../data/staticAds';
import { useFetchManagedPlacements } from '../../hooks/useFetchManagedPlacements';
import { useSiteAdsManaged } from '../../hooks/useSiteAdsManaged';
import { trackAdClick, trackAdImpression } from '../../store/actions/adActions';
import { selectAdPlacementFirst, selectAdPlacementList } from '../../store/selectors/adSelectors';
import { managedPlacementToAd, pickStaticFallback } from '../../utils/adMapper';
import { selectAdsStaticEnabled } from '../../store/reducers/sitePublicReducer';
import { makeImpressionKey, shouldTrackImpression } from '../../utils/adTracking';

const SIMILAR_BROWSE_AD_REQ = Object.freeze([
  { placement: 'inline-banner', limit: 1 },
  { placement: 'results-inline', limit: 1 },
  { placement: 'carousel', limit: 6 },
]);

const Header = styled(motion.div)`
  margin-bottom: 1.5rem;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.5rem;
`;

const IconBox = styled.div`
  width: 2.5rem;
  height: 2.5rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  background: rgba(128, 0, 0, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.maroon};
`;

const Title = styled.h1`
  font-size: ${({ theme }) => theme.fontClamp['3xl']};
  font-weight: 700;
  color: ${({ theme }) => theme.colors.foreground};
  margin: 0;
`;

const Subtitle = styled.p`
  font-size: ${({ theme }) => theme.fontClamp.sm};
  color: ${({ theme }) => theme.colors.mutedForeground};
  margin: 0 0 0 3.25rem;
`;

const Grid = styled.div`
  margin-top: 2rem;
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
  @media (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (min-width: 1024px) {
    grid-template-columns: repeat(3, 1fr);
  }
  @media (min-width: 1400px) {
    grid-template-columns: repeat(4, 1fr);
  }
`;

const Card = styled(motion.div)`
  background: ${({ theme }) => theme.colors.card};
  padding: 1.5rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  box-shadow: ${({ theme }) => theme.shadowCard};
  display: flex;
  flex-direction: column;
  gap: 1rem;
  transition: box-shadow 0.2s;
  &:hover {
    box-shadow: ${({ theme }) => theme.shadowCardHover};
  }
`;

const CardTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`;

const PosterBox = styled.div`
  width: 3rem;
  height: 3rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  background: rgba(0, 48, 135, 0.1);
  background-image: ${({ $url }) => ($url ? `url(${$url})` : 'none')};
  background-size: cover;
  background-position: center;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.primary};
`;

const ScoreBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.75rem;
  background: rgba(234, 179, 8, 0.1);
  color: ${({ theme }) => theme.colors.gold};
  border-radius: ${({ theme }) => theme.radiusSm};
  font-size: ${({ theme }) => theme.fontClamp.xs};
  font-weight: 700;
`;

const CardTitle = styled.h3`
  font-size: ${({ theme }) => theme.fontClamp.lg};
  font-weight: 700;
  margin: 0;
  color: ${({ theme }) => theme.colors.foreground};
`;

const CardDesc = styled.p`
  font-size: ${({ theme }) => theme.fontClamp.sm};
  color: ${({ theme }) => theme.colors.mutedForeground};
  margin: 0;
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const TagsWrap = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const Tag = styled.span`
  padding: 0.25rem 0.75rem;
  background: rgba(128, 0, 0, 0.1);
  color: ${({ theme }) => theme.colors.maroon};
  font-size: ${({ theme }) => theme.fontClamp.xs};
  font-weight: 500;
  border-radius: ${({ theme }) => theme.radiusSm};
`;

const CardLink = styled(Link)`
  width: 100%;
  padding: 0.75rem 0;
  border-radius: ${({ theme }) => theme.radiusSm};
  background: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.primaryForeground};
  font-weight: 600;
  font-size: ${({ theme }) => theme.fontClamp.sm};
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  text-decoration: none;
  margin-top: auto;
  &:hover {
    opacity: 0.92;
  }
`;

function pageTitleFor(mediaType, t) {
  if (mediaType === 'movies') return t('nav.similarMovies', 'Similar movies');
  if (mediaType === 'tv') return t('nav.similarTv', 'Similar TV shows');
  if (mediaType === 'anime-movies') return t('nav.similarAnimeMovies', 'Similar anime movies');
  if (mediaType === 'anime-tv') return t('nav.similarAnimeTv', 'Similar anime TV shows');
  return t('nav.similarAnime', 'Similar anime');
}

function isValidSort(v) {
  return SORT_OPTIONS.some((o) => o.value === v);
}

function normalizeType(param) {
  const t = String(param || '').toLowerCase();
  return CATEGORIES_TYPES.includes(t) ? t : null;
}

function groupForType(mediaType) {
  if (mediaType === 'anime-movies') return 'anime_movie';
  if (mediaType === 'anime-tv') return 'anime_tv';
  return mediaType;
}

function itemTypeLabel(category) {
  if (category === 'anime_movie') return 'Anime movie';
  if (category === 'anime_tv') return 'Anime TV series';
  if (category === 'tv') return 'TV series';
  return 'Movie';
}

export default function SimilarBrowse() {
  const dispatch = useDispatch();
  const { sitePublicLoaded, adsManagedEnabled } = useSiteAdsManaged();
  const adsStaticEnabled = useSelector(selectAdsStaticEnabled);
  const { items: catalogItems, loading: catalogLoading } = useSelector((s) => s.catalog);
  const { type } = useParams();
  const { pathname } = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const region = regionFromPathname(pathname);
  const serverLanguage = serverLanguageFromRegion(region);
  const { t } = useI18n();
  const pre = region || '';

  const mediaType = normalizeType(type);

  useEffect(() => {
    if (!mediaType) return;
    dispatch(
      fetchCatalog({
        limit: 120,
        group: groupForType(mediaType),
        region: pre || undefined,
        language: serverLanguage,
      }),
    );
  }, [dispatch, mediaType, pre, serverLanguage]);

  const pageParam = Math.max(1, parseInt(searchParams.get('page') || '1', 10) || 1);
  const sortParam = searchParams.get('sort') || 'default';
  const page = Number.isFinite(pageParam) ? pageParam : 1;
  const sortBy = isValidSort(sortParam) ? sortParam : 'default';

  const setPage = (p) => {
    const next = new URLSearchParams(searchParams);
    next.set('page', String(Math.max(1, p)));
    setSearchParams(next, { replace: true });
  };
  const setSort = (s) => {
    const next = new URLSearchParams(searchParams);
    next.set('sort', s);
    next.set('page', '1');
    setSearchParams(next, { replace: true });
  };

  const allItems = catalogItems || [];
  const sortedItems = useMemo(() => sortItems(allItems, sortBy), [allItems, sortBy]);
  const totalItems = sortedItems.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paginatedItems = useMemo(
    () => sortedItems.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE),
    [sortedItems, currentPage],
  );

  const { adDomain, adLocale, adDevice } = useFetchManagedPlacements('similar-browse', SIMILAR_BROWSE_AD_REQ);
  const managedBanner = useSelector((s) => selectAdPlacementFirst(s, 'similar-browse', 'inline-banner'));
  const managedInline = useSelector((s) => selectAdPlacementFirst(s, 'similar-browse', 'results-inline'));
  const managedCarousel = useSelector((s) => selectAdPlacementList(s, 'similar-browse', 'carousel'));

  const browseBannerAd = useMemo(
    () => managedPlacementToAd(managedBanner, pickStaticFallback(adsStaticEnabled, bannerAds[1] ?? bannerAds[0])),
    [managedBanner, adsStaticEnabled],
  );
  const browseInlineAd = useMemo(
    () => managedPlacementToAd(managedInline, pickStaticFallback(adsStaticEnabled, cardAds[0])),
    [managedInline, adsStaticEnabled],
  );
  const browseCarouselAds = useMemo(() => {
    if (managedCarousel.length) {
      return managedCarousel.map((x, i) =>
        managedPlacementToAd(x, pickStaticFallback(adsStaticEnabled, carouselAds[i] || carouselAds[0])),
      );
    }
    return adsStaticEnabled ? carouselAds : [];
  }, [managedCarousel, adsStaticEnabled]);

  useEffect(() => {
    if (!sitePublicLoaded || !adsManagedEnabled || !managedBanner?._id) return;
    const key = makeImpressionKey({
      campaign: managedBanner._id,
      page: 'similar-browse',
      placement: 'inline-banner',
      device: adDevice,
      domain: adDomain,
    });
    if (shouldTrackImpression(key)) {
      dispatch(
        trackAdImpression({
          campaign: managedBanner._id,
          page: 'similar-browse',
          placement: 'inline-banner',
          device: adDevice,
          domain: adDomain,
          locale: adLocale,
        }),
      );
    }
  }, [dispatch, managedBanner, adDevice, adDomain, adLocale, sitePublicLoaded, adsManagedEnabled]);

  useEffect(() => {
    if (!sitePublicLoaded || !adsManagedEnabled || !managedInline?._id) return;
    const key = makeImpressionKey({
      campaign: managedInline._id,
      page: 'similar-browse',
      placement: 'results-inline',
      device: adDevice,
      domain: adDomain,
    });
    if (shouldTrackImpression(key)) {
      dispatch(
        trackAdImpression({
          campaign: managedInline._id,
          page: 'similar-browse',
          placement: 'results-inline',
          device: adDevice,
          domain: adDomain,
          locale: adLocale,
        }),
      );
    }
  }, [dispatch, managedInline, adDevice, adDomain, adLocale, sitePublicLoaded, adsManagedEnabled]);

  const onBrowseBannerClick = useCallback(() => {
    if (!sitePublicLoaded || !adsManagedEnabled || !managedBanner?._id) return;
    dispatch(
      trackAdClick({
        campaign: managedBanner._id,
        page: 'similar-browse',
        placement: 'inline-banner',
        device: adDevice,
        domain: adDomain,
        locale: adLocale,
      }),
    );
  }, [dispatch, managedBanner, adDevice, adDomain, adLocale, sitePublicLoaded, adsManagedEnabled]);

  const onBrowseInlineClick = useCallback(() => {
    if (!sitePublicLoaded || !adsManagedEnabled || !managedInline?._id) return;
    dispatch(
      trackAdClick({
        campaign: managedInline._id,
        page: 'similar-browse',
        placement: 'results-inline',
        device: adDevice,
        domain: adDomain,
        locale: adLocale,
      }),
    );
  }, [dispatch, managedInline, adDevice, adDomain, adLocale, sitePublicLoaded, adsManagedEnabled]);

  const onBrowseCarouselVisible = useCallback(
    (visibleAd) => {
      if (!sitePublicLoaded || !adsManagedEnabled || !visibleAd?.managedCampaignId) return;
      const key = makeImpressionKey({
        campaign: visibleAd.managedCampaignId,
        page: 'similar-browse',
        placement: 'carousel',
        device: adDevice,
        domain: adDomain,
      });
      if (shouldTrackImpression(key)) {
        dispatch(
          trackAdImpression({
            campaign: visibleAd.managedCampaignId,
            page: 'similar-browse',
            placement: 'carousel',
            device: adDevice,
            domain: adDomain,
            locale: adLocale,
          }),
        );
      }
    },
    [dispatch, adDevice, adDomain, adLocale, sitePublicLoaded, adsManagedEnabled],
  );

  const onBrowseCarouselClick = useCallback(
    (_e, visibleAd) => {
      if (!sitePublicLoaded || !adsManagedEnabled || !visibleAd?.managedCampaignId) return;
      dispatch(
        trackAdClick({
          campaign: visibleAd.managedCampaignId,
          page: 'similar-browse',
          placement: 'carousel',
          device: adDevice,
          domain: adDomain,
          locale: adLocale,
        }),
      );
    },
    [dispatch, adDevice, adDomain, adLocale, sitePublicLoaded, adsManagedEnabled],
  );

  if (!mediaType) {
    return <NotFound />;
  }

  const pageTitle = pageTitleFor(mediaType, t);

  return (
    <>
      <SEO
        region={region}
        path={`/similar/${mediaType}`}
        title={`${pageTitle} - ${t('similar.browse.metaSuffix', 'Similar Picks')}`}
        description={t('similar.browse.metaDescription', 'Browse titles and open similar picks for each title.')}
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          name: `${pageTitle} Similar Picks`,
          inLanguage: region || 'en',
        }}
      />
      <PageLayout>
        <Container style={{ paddingTop: '2.5rem', paddingBottom: '2.5rem' }}>
        <Header initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
          <HeaderRow>
            <IconBox><LayoutGrid size={20} strokeWidth={2.5} /></IconBox>
            <Title>{pageTitle}</Title>
          </HeaderRow>
          <Subtitle>
            {t('similar.browse.subtitlePrefix', 'Browse')} {totalItems} {t('similar.browse.subtitleTitles', 'titles')} — {t('similar.browse.subtitleOpen', 'open')} <strong>{t('similar.browse.subtitlePicks', 'similar picks')}</strong> {t('similar.browse.subtitleAny', 'for any title')}
            {catalogLoading ? ' (loading…)' : ''}.
          </Subtitle>
        </Header>

        <div style={{ margin: '1.25rem 0' }}>
          <StaticAdBanner ad={browseBannerAd} variant="inline" pageKey="similar-browse" onLinkClick={onBrowseBannerClick} />
        </div>

        {catalogLoading && !catalogItems.length ? (
          <p style={{ color: 'var(--muted-foreground)' }}>{t('similar.browse.loadingCatalog', 'Loading catalog...')}</p>
        ) : null}

        <SortBar
          sortBy={sortBy}
          onSortChange={setSort}
          totalItems={totalItems}
          page={currentPage}
          pageSize={PAGE_SIZE}
        />
        <div style={{ margin: '1.25rem 0' }}>
          <AdInline ad={browseInlineAd} pageKey="similar-browse" onLinkClick={onBrowseInlineClick} />
        </div>
        <Grid>
          {!catalogLoading && totalItems === 0 ? (
            <p style={{ gridColumn: '1 / -1', color: 'var(--muted-foreground)' }}>
              {t('similar.browse.empty', 'No titles available for this category yet.')}
            </p>
          ) : null}
          {paginatedItems.map((it, index) => (
            <Card
              key={`${it.category}-${it.tmdbId}`}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: Math.min(index * 0.04, 0.3) }}
            >
              <CardTop>
                <PosterBox $url={it.poster}>{it.poster ? '' : it.title.charAt(0)}</PosterBox>
                <ScoreBadge><Star size={12} fill="currentColor" /> {t('similar.browse.similarBadge', 'Similar')}</ScoreBadge>
              </CardTop>
              <CardTitle>{it.title}</CardTitle>
              <CardDesc>{it.overview}</CardDesc>
              <TagsWrap>
                <Tag>{pageTitle}</Tag>
                <Tag>{itemTypeLabel(it.category)}</Tag>
                <Tag>{t('similar.browse.communityPicks', 'Community picks')}</Tag>
              </TagsWrap>
              <CardLink to={similarPath(it.category, it.tmdbId, it.title, pre)}>
                {t('similar.browse.seePicks', 'See similar picks')} <ArrowRight size={14} />
              </CardLink>
            </Card>
          ))}
        </Grid>
        <div style={{ marginTop: '2rem' }}>
          <AdCarousel
            ads={browseCarouselAds}
            pageKey="similar-browse"
            onVisibleAd={onBrowseCarouselVisible}
            onAdClick={onBrowseCarouselClick}
          />
        </div>
        <Pagination totalItems={totalItems} currentPage={currentPage} onPageChange={setPage} />
        </Container>
      </PageLayout>
    </>
  );
}
